import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProfile, saveProfile, type UserProfile } from '../services/profileApi';

type FormState = Omit<UserProfile, 'userId' | 'updatedAt'>;

const DEFAULT_FORM: FormState = {
  displayName: '',
  dateOfBirth: '',
  heightIn: undefined,
  weightUnit: 'lbs',
  notifications: { enabled: false, reminderTime: '' },
};

export default function Profile() {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const profile = await getProfile();
        if (profile) {
          setForm({
            displayName: profile.displayName ?? '',
            dateOfBirth: profile.dateOfBirth ?? '',
            heightIn: profile.heightIn,
            weightUnit: profile.weightUnit,
            notifications: {
              enabled: profile.notifications.enabled,
              reminderTime: profile.notifications.reminderTime ?? '',
            },
          });
        }
      } catch {
        showToast('error', 'Failed to load profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveProfile({
        ...form,
        displayName: form.displayName || undefined,
        dateOfBirth: form.dateOfBirth || undefined,
        notifications: {
          enabled: form.notifications.enabled,
          reminderTime: form.notifications.reminderTime || undefined,
        },
      });
      showToast('success', 'Profile saved.');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <Link to="/" className="font-semibold text-gray-900 tracking-tight hover:text-indigo-600 transition">
          glp1-tracker
        </Link>
        <span className="text-sm text-gray-500">Profile &amp; Settings</span>
      </header>

      <main className="flex-1 max-w-lg w-full mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Profile &amp; Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Your personal information and preferences.</p>
        </div>

        {toast && (
          <div
            className={`mb-4 rounded-xl px-4 py-3 text-sm ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-6">
          {/* Personal info */}
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Personal info</h2>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="displayName">
                Display name
              </label>
              <input
                id="displayName"
                type="text"
                value={form.displayName ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder="Your name"
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="dateOfBirth">
                Date of birth
              </label>
              <input
                id="dateOfBirth"
                type="date"
                value={form.dateOfBirth ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="heightIn">
                Height (inches)
              </label>
              <input
                id="heightIn"
                type="number"
                min={1}
                max={120}
                step={0.5}
                value={form.heightIn ?? ''}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    heightIn: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
                placeholder="e.g. 68"
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </section>

          {/* Preferences */}
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Preferences</h2>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="weightUnit">
                Weight unit
              </label>
              <select
                id="weightUnit"
                value={form.weightUnit}
                onChange={(e) =>
                  setForm((f) => ({ ...f, weightUnit: e.target.value as 'lbs' | 'kg' }))
                }
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="lbs">Pounds (lbs)</option>
                <option value="kg">Kilograms (kg)</option>
              </select>
            </div>
          </section>

          {/* Notifications */}
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Notifications</h2>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.notifications.enabled}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    notifications: { ...f.notifications, enabled: e.target.checked },
                  }))
                }
                className="w-4 h-4 accent-indigo-600"
              />
              <span className="text-sm text-gray-700">Enable daily reminders</span>
            </label>

            {form.notifications.enabled && (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="reminderTime">
                  Reminder time
                </label>
                <input
                  id="reminderTime"
                  type="time"
                  value={form.notifications.reminderTime ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      notifications: { ...f.notifications, reminderTime: e.target.value },
                    }))
                  }
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-36"
                />
              </div>
            )}
          </section>

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed self-end"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </main>
    </div>
  );
}
